/**
 * Custom pendant builder (letter/number/single-letter custom products —
 * see letter-pendant-detect.liquid for the product list).
 *
 * 'letters'/'numbers' modes: the Shopify count variant option ("Letters":
 * 1 Letter … 10 Letters, or "Numbers": 1 Number … N Numbers) controls real
 * variant pricing. This component hides that selector and maps sanitized
 * character count to the matching variant while storing the typed characters
 * on the line item as properties[Custom Letters] / properties[Custom Numbers]
 * and properties[Character Count].
 *
 * 'single' mode: the product has one variant per letter ("Letter": A … Z);
 * the typed letter maps directly to that option value, so no line item
 * properties are used.
 *
 * Property-only mode (data-property-only="true"): the product has NO count
 * option because the letter count does not change the price. The typed
 * characters ride on the line item as properties[Letters] / [Number] +
 * [Character Count]; the Shopify variant is whatever the normal picker
 * (Color / Length / Diamond Type) resolves. Max length comes from the product
 * metafield custom.personalization_max_chars (data-max-length).
 *
 * data-input-mode="letters" (default) accepts A–Z only;
 * data-input-mode="numbers" accepts 0–9 only;
 * data-input-mode="single" accepts a single A–Z letter.
 */
if (!customElements.get('letter-pendant-builder')) {
  customElements.define(
    'letter-pendant-builder',
    class LetterPendantBuilder extends HTMLElement {
      static DEFAULT_MAX_LENGTH = 10;

      connectedCallback() {
        // Note: don't use `this.inputMode` — that's a native reflected DOM
        // property limited to known values ("text", "numeric", …), so
        // assigning "numbers"/"letters" silently reads back as "".
        this.builderMode = ['numbers', 'single'].includes(this.dataset.inputMode)
          ? this.dataset.inputMode
          : 'letters';
        this.propertyOnly = this.dataset.propertyOnly === 'true';
        this.maxCharacters =
          parseInt(this.dataset.maxLength, 10) > 0
            ? parseInt(this.dataset.maxLength, 10)
            : LetterPendantBuilder.DEFAULT_MAX_LENGTH;
        this.input = this.querySelector('[data-letter-input]');
        this.statusEl = this.querySelector('[data-letter-builder-status]');
        this.hiddenFieldset = this.querySelector('.letter-pendant-builder__letters-inputs');
        this.variantSelects = this.closest('variant-selects');
        this.productInfo = this.closest('product-info');
        this.productForm = this.productInfo?.querySelector('product-form');
        this.variantIdInput = this.productInfo?.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
        this.customLettersProperty = this.productInfo?.querySelector('[data-letter-property="custom"]');
        this.countPropertyInput = this.productInfo?.querySelector('[data-letter-property="count"]');
        this.lettersOptionPosition = parseInt(this.dataset.lettersOptionPosition, 10);
        this.colorOptionPosition = this.dataset.colorOptionPosition
          ? parseInt(this.dataset.colorOptionPosition, 10)
          : null;

        this.letterOptionValues = this.readLetterOptionValues();

        this.onInput = this.onInput.bind(this);
        this.onVariantSelectChange = this.onVariantSelectChange.bind(this);

        this.input?.addEventListener('input', this.onInput);
        this.variantSelects?.addEventListener('change', this.onVariantSelectChange);

        this.uncheckLetterRadios();
        this.setHiddenRadiosUnfocusable();
        this.syncFromInput();
      }

      setHiddenRadiosUnfocusable() {
        // The Letters fieldset is visually hidden and aria-hidden, but enabled
        // radios would otherwise stay keyboard-focusable. Keep them enabled
        // (variant matching reads checked/disabled state) but out of the tab
        // order; the visible text input is the only user-facing control.
        this.hiddenFieldset?.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.tabIndex = -1;
        });
      }

      disconnectedCallback() {
        this.input?.removeEventListener('input', this.onInput);
        this.variantSelects?.removeEventListener('change', this.onVariantSelectChange);
        clearTimeout(this._urlSyncTimer);
      }

      readLetterOptionValues() {
        const source = this.querySelector('[data-letter-option-values]');
        if (!source?.textContent) return [];

        try {
          const parsed = JSON.parse(source.textContent);
          return parsed
            .map((value) => {
              if (typeof value === 'string') return value;
              if (value && typeof value.name === 'string') return value.name;
              return '';
            })
            .filter(Boolean);
        } catch (error) {
          return [];
        }
      }

      sanitize(rawValue) {
        const value = String(rawValue || '');
        const sanitized =
          this.builderMode === 'numbers'
            ? value.replace(/[^0-9]/g, '')
            : value.toUpperCase().replace(/[^A-Z]/g, '');
        return sanitized.slice(0, this.maxCharacters);
      }

      getLetterOptionValue(sanitized) {
        if (!sanitized) return null;

        // Single-letter products: the option value IS the typed letter (A–Z).
        if (this.builderMode === 'single') {
          return (
            this.letterOptionValues.find((value) => String(value).trim().toUpperCase() === sanitized) || null
          );
        }

        const count = sanitized.length;
        const noun = this.builderMode === 'numbers' ? 'Number' : 'Letter';
        const candidates = [`${count} ${noun}`, `${count} ${noun}s`];

        for (const candidate of candidates) {
          if (this.letterOptionValues.includes(candidate)) return candidate;
        }

        return (
          this.letterOptionValues.find((value) => {
            const match = String(value)
              .trim()
              .match(/^(\d+)(?:\s+(?:Letter|Number|Digit|Character)s?)?$/i);
            return match && parseInt(match[1], 10) === count;
          }) || null
        );
      }

      getSelectedColorValue() {
        if (!this.colorOptionPosition || !this.variantSelects) return null;

        const colorGroup = this.variantSelects.querySelector(
          `[data-option-position="${this.colorOptionPosition}"]`
        );
        if (!colorGroup) return null;

        const checked = colorGroup.querySelector('input[type="radio"]:checked');
        if (checked?.value) return checked.value;

        const select = colorGroup.querySelector('select');
        return select?.value || null;
      }

      findLetterRadio(optionValue) {
        if (!optionValue || !this.hiddenFieldset) return null;

        return Array.from(this.hiddenFieldset.querySelectorAll('input[type="radio"]')).find(
          (input) => input.value === optionValue
        );
      }

      uncheckLetterRadios() {
        this.hiddenFieldset?.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.checked = false;
        });
      }

      getVariantData() {
        return this.variantSelects?.getVariantData?.() || [];
      }

      getSelectedOtherOptionValues() {
        // Every real picker option other than the hidden Letters/Numbers
        // option (Color, Length, Diamond Type, …) constrains the variant match.
        const selected = {};
        if (!this.variantSelects) return selected;

        this.variantSelects.querySelectorAll('[data-option-position]').forEach((group) => {
          const position = parseInt(group.dataset.optionPosition, 10);
          if (!position || position === this.lettersOptionPosition) return;
          if (this.hiddenFieldset && this.hiddenFieldset.contains(group)) return;

          const checked = group.querySelector('input[type="radio"]:checked');
          const value = checked?.value || group.querySelector('select')?.value || null;
          if (value) selected[position] = value;
        });

        if (!Object.keys(selected).length && this.colorOptionPosition) {
          const color = this.getSelectedColorValue();
          if (color) selected[this.colorOptionPosition] = color;
        }

        return selected;
      }

      findMatchingVariant(letterOptionValue) {
        const variants = this.getVariantData();
        if (!variants.length || !letterOptionValue) return null;

        const selectedOthers = this.getSelectedOtherOptionValues();
        const lettersKey = `option${this.lettersOptionPosition}`;

        return (
          variants.find((variant) => {
            if (variant[lettersKey] !== letterOptionValue) return false;

            for (const [position, value] of Object.entries(selectedOthers)) {
              if (variant[`option${position}`] !== value) return false;
            }

            return true;
          }) || null
        );
      }

      validate(sanitized) {
        const count = sanitized.length;

        if (!count) return false;
        if (count > this.maxCharacters) return false;
        if (this.propertyOnly) return true;

        const letterOptionValue = this.getLetterOptionValue(sanitized);
        if (!letterOptionValue) return false;

        const variant = this.findMatchingVariant(letterOptionValue);
        if (!variant || !variant.available) return false;

        return true;
      }

      updateLineItemProperties(sanitized) {
        const count = sanitized.length;

        if (this.customLettersProperty) {
          this.customLettersProperty.value = sanitized;
        }

        if (this.countPropertyInput) {
          this.countPropertyInput.value = count ? String(count) : '';
        }
      }

      setAddToCartEnabled(isEnabled) {
        if (isEnabled) {
          this.variantIdInput?.removeAttribute('disabled');
          this.productForm?.toggleSubmitButton?.(false);
          return;
        }

        if (this.variantIdInput) {
          this.variantIdInput.disabled = true;
        }

        this.productForm?.toggleSubmitButton?.(true);
      }

      applyVariantUpdate(variant) {
        if (!variant || !this.productInfo) return;

        // Dedupe: syncFromInput can be invoked more than once per user action
        // (builder's own change listener + product-info's option-change
        // handler). Only the first call for a given variant applies the
        // instant update, so price/media/URL/pubsub fire once per action.
        if (String(this._lastAppliedVariantId) === String(variant.id)) return;
        this._lastAppliedVariantId = variant.id;

        const isTyping = this.input && document.activeElement === this.input;

        this.variantSelects?.updateSelectedVariant?.(variant);
        // Skip the synchronous history.replaceState while typing (Safari
        // rate-limits history calls); debounce a single trailing URL sync.
        this.productInfo.applyInstantVariantUpdate(variant, this.productInfo.dataset.url, {
          updateUrl: !isTyping,
        });

        if (isTyping) this.scheduleUrlSync(variant.id);
      }

      scheduleUrlSync(variantId) {
        clearTimeout(this._urlSyncTimer);
        this._urlSyncTimer = setTimeout(() => {
          this.productInfo?.updateURL?.(this.productInfo.dataset.url, variantId);
        }, 500);
      }

      setStatus(message) {
        if (!this.statusEl) return;
        this.statusEl.textContent = message || '';
        this.statusEl.hidden = !message;
      }

      getInvalidReason(sanitized) {
        if (!sanitized.length) return this.propertyOnly ? 'Type your letters to continue.' : '';

        const plural = this.maxCharacters === 1 ? 'character' : 'characters';
        if (sanitized.length > this.maxCharacters) return `We can make up to ${this.maxCharacters} ${plural}.`;

        const letterOptionValue = this.getLetterOptionValue(sanitized);
        if (!letterOptionValue) return `We can make up to ${this.maxCharacters} ${plural}.`;

        return 'That length is currently unavailable.';
      }

      applyInvalidState(sanitized = '') {
        this.uncheckLetterRadios();
        this.setAddToCartEnabled(false);
        this.setStatus(this.getInvalidReason(sanitized));
      }

      syncFromInput() {
        const sanitized = this.sanitize(this.input?.value || '');

        if (this.input && this.input.value !== sanitized) {
          const cursor = this.input.selectionStart;
          this.input.value = sanitized;
          const nextCursor = Math.min(cursor ?? sanitized.length, sanitized.length);
          this.input.setSelectionRange(nextCursor, nextCursor);
        }

        this.updateLineItemProperties(sanitized);

        if (!this.validate(sanitized)) {
          this.applyInvalidState(sanitized);
          return;
        }

        if (this.propertyOnly) {
          // Variant comes from the real picker; the builder only gates the
          // add-to-cart on having valid characters and keeps price/media in
          // sync with the currently selected variant.
          const currentVariant = this.variantSelects?.getCurrentVariant?.();
          if (currentVariant) {
            if (this.variantIdInput) this.variantIdInput.value = currentVariant.id;
            this.setAddToCartEnabled(currentVariant.available);
            this.setStatus('');
            this.applyVariantUpdate(currentVariant);
          } else {
            this.setAddToCartEnabled(false);
            this.setStatus('');
            this.productInfo?.applyUnavailableInstantUpdate?.(this.productInfo.dataset.url);
          }
          return;
        }

        const letterOptionValue = this.getLetterOptionValue(sanitized);
        const radio = this.findLetterRadio(letterOptionValue);

        if (!radio || radio.disabled) {
          this.applyInvalidState(sanitized);
          return;
        }

        radio.checked = true;

        const variant = this.findMatchingVariant(letterOptionValue);
        if (variant) {
          if (this.variantIdInput) this.variantIdInput.value = variant.id;
          this.setAddToCartEnabled(true);
          this.setStatus('');
          this.applyVariantUpdate(variant);
        } else {
          this.applyInvalidState(sanitized);
        }
      }

      onInput() {
        this.syncFromInput();
      }

      onVariantSelectChange(event) {
        if (event.target.closest('.letter-pendant-builder__letters-inputs')) return;
        this.syncFromInput();
      }
    }
  );
}
